import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteField,
  doc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { HCard, SectionLabel } from '../h';
import { SettingRow } from '../Shell/settingControls';

/* Higlo-level settings section, rendered inside HigloDetail for controllers.

   The igloo twin of DeviceSettingsView, discovered entirely at runtime:

     1. Live-subscribe to iglooSettings/everyControllerAndEveryIglooVisible/
        settings — every category doc there holds ONLY controller-controllable
        settings (deploy-igloo-settings.js routes them by the
        controllerControllable flag in master-igloo-settings.json). Fields on
        each doc are keyed by settingName, each { type, value, label,
        description, tooltip, choices?, range?, step?, order }. Unlike the
        device screen there is no per-APK-version allowlist — membership of
        this doc IS the exposure gate — and no category headers: the settings
        render as one flat list sorted by the schema's global `order`.

     2. Live-subscribe to iglooInfo/{iglooID}/userSettings — the
        controller-set values. A setting is "on Default" when no user value
        exists; explicit values are written { [name]: value } (merge) and
        cleared with deleteField(), exactly like the device tier. On the
        Hearth the resolution is user ?? adminOverride ?? global.

     3. Live-subscribe to iglooInfo/{iglooID}/overrideSettings — the ADMIN
        overrides — so the "Default" position shows what the Higlo will
        actually use (adminOverride ?? global), not the bare global.

   Fail-soft by design: if the catalog or userSettings read errors (rules not
   deployed yet, transient Firestore failure), the whole section renders
   nothing — the rest of HigloDetail must stay fully usable. If only the
   overrideSettings read errors, we degrade to showing the bare global as
   the default. Nothing here throws.

   The parent renders this only for controllers (isCallerController) — the
   security rules would reject writes from non-controllers anyway. */
export function HigloSettingsSection({ iglooID }) {
  // null = still loading, [] = loaded-but-empty, Array = loaded.
  const [catalogSettings, setCatalogSettings] = useState(null);
  const [catalogFailed, setCatalogFailed] = useState(false);
  // null = still loading; { [category]: { [name]: value } } once loaded.
  const [userValuesByCategory, setUserValuesByCategory] = useState(null);
  const [userFailed, setUserFailed] = useState(false);
  // Admin overrides — {} until loaded; errors degrade to {} (fail-soft).
  const [adminOverridesByCategory, setAdminOverridesByCategory] = useState({});

  // 1. Controller-visible catalog (global tier).
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'iglooSettings', 'everyControllerAndEveryIglooVisible', 'settings'),
      (snap) => {
        const settings = [];
        snap.forEach((catDoc) => {
          const category = catDoc.id;
          const data = catDoc.data() || {};
          for (const [name, entry] of Object.entries(data)) {
            if (!entry || typeof entry !== 'object' || entry.value === undefined) continue;
            settings.push({ ...entry, name, category });
          }
        });
        settings.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setCatalogSettings(settings);
      },
      (err) => {
        console.warn('HigloSettingsSection catalog listener (fail-soft)', err?.code || err);
        setCatalogFailed(true);
      },
    );
    return () => { try { unsub(); } catch (_) {} };
  }, []);

  // 2. Controller-set values (user tier).
  useEffect(() => {
    if (!iglooID) return undefined;
    const unsub = onSnapshot(
      collection(db, 'iglooInfo', iglooID, 'userSettings'),
      (snap) => {
        const byCategory = {};
        snap.forEach((catDoc) => { byCategory[catDoc.id] = catDoc.data() || {}; });
        setUserValuesByCategory(byCategory);
      },
      (err) => {
        console.warn('HigloSettingsSection userSettings listener (fail-soft)', err?.code || err);
        setUserFailed(true);
      },
    );
    return () => { try { unsub(); } catch (_) {} };
  }, [iglooID]);

  // 3. Admin overrides (override tier) — what "Default" resolves to.
  useEffect(() => {
    if (!iglooID) return undefined;
    const unsub = onSnapshot(
      collection(db, 'iglooInfo', iglooID, 'overrideSettings'),
      (snap) => {
        const byCategory = {};
        snap.forEach((catDoc) => { byCategory[catDoc.id] = catDoc.data() || {}; });
        setAdminOverridesByCategory(byCategory);
      },
      (err) => {
        // Degrade to bare globals — most likely the updated rules aren't
        // deployed yet. The section stays functional.
        console.warn('HigloSettingsSection overrideSettings listener (fail-soft)', err?.code || err);
        setAdminOverridesByCategory({});
      },
    );
    return () => { try { unsub(); } catch (_) {} };
  }, [iglooID]);

  const effectiveDefaultFor = (setting) => {
    const adminValue = adminOverridesByCategory[setting.category]?.[setting.name];
    return adminValue !== undefined ? adminValue : setting.value;
  };

  const saveValue = async (category, name, value) => {
    try {
      await setDoc(
        doc(db, 'iglooInfo', iglooID, 'userSettings', category),
        { [name]: value },
        { merge: true },
      );
    } catch (err) {
      console.error('HigloSettingsSection saveValue error', err);
    }
  };

  const clearValue = async (category, name) => {
    try {
      await setDoc(
        doc(db, 'iglooInfo', iglooID, 'userSettings', category),
        { [name]: deleteField() },
        { merge: true },
      );
    } catch (err) {
      console.error('HigloSettingsSection clearValue error', err);
    }
  };

  const settings = useMemo(
    () => (Array.isArray(catalogSettings) ? catalogSettings : []),
    [catalogSettings],
  );

  /* Nothing to show: still loading, load failed, or no controller-
     controllable settings deployed. In every one of those cases the section
     disappears entirely rather than rendering a broken or empty card. */
  if (catalogFailed || userFailed) return null;
  if (catalogSettings === null || userValuesByCategory === null) return null;
  if (settings.length === 0) return null;

  return (
    <>
      <SectionLabel className="mb-2">Settings</SectionLabel>
      <p className="text-[12px] text-slate-500 leading-relaxed mb-2">
        These settings apply to the whole Higlo — every Hearth in it uses the
        same values.
      </p>
      <HCard className="mb-4 overflow-hidden">
        {settings.map((setting, idx) => (
          <SettingRow
            key={`${setting.category}/${setting.name}`}
            setting={setting}
            userValue={userValuesByCategory[setting.category]?.[setting.name]}
            defaultValue={effectiveDefaultFor(setting)}
            divider={idx < settings.length - 1}
            onToggleCustom={(custom) => {
              if (custom) {
                saveValue(setting.category, setting.name, effectiveDefaultFor(setting));
              } else {
                clearValue(setting.category, setting.name);
              }
            }}
            onChange={(v) => saveValue(setting.category, setting.name, v)}
          />
        ))}
      </HCard>
    </>
  );
}

export default HigloSettingsSection;
